// Story 021 — Navigator/Overview filter store. Single source of truth for
// the cross-widget filter cascade. Filmstrip / Tiles / List / Sketch all
// subscribe to this store and re-render their visible set in lockstep
// when the user toggles a chip in the Navigator widget.
//
// State persistence: URL search params drive this store so bookmarks
// restore. The Navigator widget reads/writes via React Router's
// useSearchParams (mounted in NavigatorWidget.tsx) — the store stays
// pure-Zustand to keep widgets that read but don't write off the URL.
//
// Param shape (per .vibe/19 §6 Q9):
//   ?blades=A,B&sides=LE,SS&sev=4,5&status=detected,reviewed
//     &visibility=seen,unseen&q=erosion
//
// AND across rows, OR within a row.
//
// R58-L-04 — `view` dimension generalised from {all,unseen,linked} →
//   `visibility: ReviewVisibilityKey[]` (multi-select Seen / Unseen),
//   OQ-1 (b). The legacy 'linked' option is dropped entirely (OQ-8 a);
//   empty array means "all" (bypass).
// R58-L-05 — `q` text-search axis. AND-composes with the rest of the
//   filter tree (OQ-9 b). Empty/whitespace-only = bypass.
import { create } from "zustand";

export type SideKey = "LE" | "TE" | "PS" | "SS";
/**
 * R58-L-04 OQ-1 (b) — visibility axis is multi-select Seen / Unseen.
 * Empty array → bypass (show all images regardless of review state).
 * Both values checked → still bypass (the union of seen + unseen is
 * the entire set).
 *
 * The legacy `ViewKey` ('all' | 'unseen' | 'linked') is kept as a type
 * alias for any read-only legacy URL hydration path; new code should
 * use ReviewVisibilityKey directly.
 */
export type ReviewVisibilityKey = "seen" | "unseen";
export type ViewKey = "all" | "unseen" | "linked";

export interface AnalysisFilterState {
  blades: readonly string[];
  sides: readonly SideKey[];
  /** 1..5 numeric severity buckets (resolved class_category). */
  severities: readonly number[];
  /** Free-form status keys (the BE's defects.status values). MVP keeps
   *  this open-ended so the store doesn't need a contract bump every
   *  time the BE narrows the enum (.vibe/23 Q1 still pending). */
  statuses: readonly string[];
  /**
   * R58-L-04 — multi-select visibility ('seen' / 'unseen'). Empty = no
   * narrowing. The Linked option from the legacy ViewKey is removed
   * entirely (OQ-8 a).
   */
  visibility: readonly ReviewVisibilityKey[];
  /**
   * R58-L-05 — trimmed defect-class text-search term. Empty = no
   * narrowing. The store keeps the raw user value (trimmed); consumers
   * push it through their query hook's `q` param which sends to the BE.
   */
  q: string;

  setBlades: (next: readonly string[]) => void;
  setSides: (next: readonly SideKey[]) => void;
  setSeverities: (next: readonly number[]) => void;
  setStatuses: (next: readonly string[]) => void;
  setVisibility: (next: readonly ReviewVisibilityKey[]) => void;
  setQ: (next: string) => void;
  reset: () => void;
  /** Atomically replace every dimension. Used by the URL-sync
   *  initializer in NavigatorWidget so a single subscribe broadcast
   *  fires instead of one-per-row. */
  hydrate: (state: {
    blades: readonly string[];
    sides: readonly SideKey[];
    severities: readonly number[];
    statuses: readonly string[];
    visibility: readonly ReviewVisibilityKey[];
    q: string;
  }) => void;
}

const EMPTY_BLADES: readonly string[] = [];
const EMPTY_SIDES: readonly SideKey[] = [];
const EMPTY_SEVS: readonly number[] = [];
const EMPTY_STATUSES: readonly string[] = [];
const EMPTY_VISIBILITY: readonly ReviewVisibilityKey[] = [];

export const useAnalysisFilterStore = create<AnalysisFilterState>((set) => ({
  blades: EMPTY_BLADES,
  sides: EMPTY_SIDES,
  severities: EMPTY_SEVS,
  statuses: EMPTY_STATUSES,
  visibility: EMPTY_VISIBILITY,
  q: "",

  setBlades: (next) => {
    set({ blades: next });
  },
  setSides: (next) => {
    set({ sides: next });
  },
  setSeverities: (next) => {
    set({ severities: next });
  },
  setStatuses: (next) => {
    set({ statuses: next });
  },
  setVisibility: (next) => {
    set({ visibility: next });
  },
  setQ: (next) => {
    set({ q: next });
  },
  reset: () => {
    set({
      blades: EMPTY_BLADES,
      sides: EMPTY_SIDES,
      severities: EMPTY_SEVS,
      statuses: EMPTY_STATUSES,
      visibility: EMPTY_VISIBILITY,
      q: "",
    });
  },
  hydrate: (next) => {
    set(next);
  },
}));

/**
 * Apply the current filter state to a defect array. Pure — no React.
 * Used by every widget that needs the visible-set cascade. AND across
 * rows; OR within each row.
 */
export interface FilterableDefect {
  bladeId?: string | null;
  side?: SideKey | null;
  classCategory?: number | null;
  status?: string | null;
}

export function applyFilters<T extends FilterableDefect>(
  items: readonly T[],
  state: Pick<
    AnalysisFilterState,
    "blades" | "sides" | "severities" | "statuses"
  >,
): readonly T[] {
  return items.filter((item) => {
    if (state.blades.length > 0) {
      if (!item.bladeId || !state.blades.includes(item.bladeId)) return false;
    }
    if (state.sides.length > 0) {
      if (!item.side || !state.sides.includes(item.side)) return false;
    }
    if (state.severities.length > 0) {
      if (
        item.classCategory == null ||
        !state.severities.includes(item.classCategory)
      ) {
        return false;
      }
    }
    if (state.statuses.length > 0) {
      if (!item.status || !state.statuses.includes(item.status)) return false;
    }
    return true;
  });
}

/**
 * Compute distinct values + counts on the *unfiltered* item set, per
 * `.vibe/19 §3` decision: counts on chips reflect the total inventory,
 * not the filtered subset, so the user can see "I have 3 defects on
 * Blade B" before deciding to toggle the chip.
 */
export function computeCounts(items: readonly FilterableDefect[]): {
  blades: Map<string, number>;
  sides: Map<SideKey, number>;
  severities: Map<number, number>;
  statuses: Map<string, number>;
} {
  const blades = new Map<string, number>();
  const sides = new Map<SideKey, number>();
  const severities = new Map<number, number>();
  const statuses = new Map<string, number>();
  for (const it of items) {
    if (it.bladeId) blades.set(it.bladeId, (blades.get(it.bladeId) ?? 0) + 1);
    if (it.side) sides.set(it.side, (sides.get(it.side) ?? 0) + 1);
    if (it.classCategory != null) {
      severities.set(
        it.classCategory,
        (severities.get(it.classCategory) ?? 0) + 1,
      );
    }
    if (it.status) statuses.set(it.status, (statuses.get(it.status) ?? 0) + 1);
  }
  return { blades, sides, severities, statuses };
}
